import type { SoftphoneOnCallHeaderProps } from './index.types';
import { ReactComponent as BackIcon } from 'Assets/icons/back.svg';
import { useSoftphoneKeypad, useSoftphoneOnCallHeader } from 'Modules/softphone/hooks';
import React from 'react';

/**
 * SoftphoneOnCallHeader Component
 * Reusable header section displaying caller information and back button during active calls
 * Used by both SoftphoneKeypad (in-call DTMF) and SoftphoneDialpad (pre-call with active calls)
 */
export const SoftphoneOnCallHeader = ({
  testID,
  onClose,
}: SoftphoneOnCallHeaderProps): React.ReactElement => {
  const { callerName, callerNumber, callDuration } = useSoftphoneOnCallHeader();
  const { handleClose } = useSoftphoneKeypad();
  const onCallHeaderTestId = testID ? `${testID}-onCall-header` : 'onCall-header';

  const displayName = (callerName || callerNumber || 'Unknown');

  // Use provided onClose or fallback to handleClose from keypad hook
  const handleBackClick = onClose || handleClose;

  return (
    <header
      className="w-full flex items-center gap-[8px] py-[8px]"
      data-testid={`${onCallHeaderTestId}-header`}
    >
      <button
        onClick={handleBackClick}
        className="flex items-center justify-center size-[24px] transition-colors flex-shrink-0 cursor-pointer"
        aria-label="Back"
        data-testid={`${onCallHeaderTestId}-back`}
      >
        <BackIcon className="size-[20px]" />
      </button>

      {displayName && (
        <h3
          className="flex-1 text-[14px] font-medium text-foreground truncate leading-[19px]"
          title={displayName}
          aria-label={`Caller: ${displayName}`}
          data-testid={`${onCallHeaderTestId}-name`}
        >
          {displayName}
        </h3>
      )}

      {callDuration && (
        <time
          className="text-[14px] text-foreground leading-[19px] flex-shrink-0"
          aria-label={`Call duration: ${callDuration}`}
          data-testid={`${onCallHeaderTestId}-duration`}
          role="time"
        >
          {callDuration}
        </time>
      )}
    </header>
  );
};
